
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AdminPageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalRecords: number;
  recordsPerPage: number;
  onDownload: (startPage: number, endPage: number) => void;
  reportTitle: string;
}

const AdminPageSelectionDialog: React.FC<AdminPageSelectionDialogProps> = ({
  isOpen,
  onClose,
  totalRecords,
  recordsPerPage,
  onDownload,
  reportTitle
}) => {
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  
  useEffect(() => {
    if (isOpen) {
      setStartPage(1);
      setEndPage(totalPages);
    }
  }, [isOpen, totalPages]);
  
  const handleDownload = () => {
    const validStartPage = Math.max(1, Math.min(startPage, totalPages));
    const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
    
    onDownload(validStartPage, validEndPage);
  };
  
  const handleStartPageChange = (value: string) => {
    const page = parseInt(value) || 1;
    const validPage = Math.max(1, Math.min(page, totalPages));
    setStartPage(validPage);
    
    if (validPage > endPage) {
      setEndPage(validPage);
    }
  };
  
  const handleEndPageChange = (value: string) => {
    const page = parseInt(value) || 1;
    const validPage = Math.max(startPage, Math.min(page, totalPages));
    setEndPage(validPage);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Pages to Download</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            <p><strong>{reportTitle}</strong></p>
            <p>Total Records: {totalRecords}</p>
            <p>Records per Page: {recordsPerPage}</p>
            <p>Total Pages: {totalPages}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startPage">Start Page</Label>
              <Input
                id="startPage"
                type="number"
                min="1"
                max={totalPages}
                value={startPage}
                onChange={(e) => handleStartPageChange(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="endPage">End Page</Label>
              <Input
                id="endPage"
                type="number"
                min={startPage}
                max={totalPages}
                value={endPage}
                onChange={(e) => handleEndPageChange(e.target.value)}
              />
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            {startPage === endPage ? (
              <p>Downloading page {startPage} ({recordsPerPage} records)</p>
            ) : (
              <p>Downloading pages {startPage}-{endPage} ({((endPage - startPage + 1) * recordsPerPage)} records)</p>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload}>
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPageSelectionDialog;
